import { z } from 'zod';
import { DateTimeGranularity, parseDateTimeInput, validateDateInputString } from '@/utilities/date';
import { fieldTypeSchema, metadataOptionsSchema } from '@/types/metadata';
import { paginationSchema } from '@/types/pagination';

const linkFolderSchema = z.object({
  id: z.string(),
  name: z.string(),
});

export const linkSchema = z.object({
  id: z.string(),
  url: z.string(),
  emails: z.array(z.string()),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
  createdBy: z.object({ id: z.string(), name: z.string() }),
  folder: linkFolderSchema,
});

export const linkListSchema = z.object({
  links: z.array(linkSchema),
  pagination: paginationSchema,
});

export const linkCreateResultSchema = z.object({
  links: z.array(linkSchema),
});

const resolvedLinkWorkflowStatusSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  color: z.string(),
});

const resolvedLinkWorkflowSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  statuses: z.array(resolvedLinkWorkflowStatusSchema),
});

const resolvedLinkMetadataDefinitionSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  type: fieldTypeSchema,
  options: metadataOptionsSchema.optional(),
});

export const resolvedLinkSchema = z.object({
  id: z.string(),
  folder: linkFolderSchema,
  tenantName: z.string(),
  inviterName: z.string(),
  expiresAt: z.string().nullable(),
  workflows: z.array(resolvedLinkWorkflowSchema),
  metadataDefinitions: z.array(resolvedLinkMetadataDefinitionSchema),
});

export const createLinkParamsSchema = z.object({
  nodeId: z.string(),
  body: z.object({
    emails: z.array(z.string().email()).min(1),
    expiresAt: z.string().optional().nullable(),
  }),
});

export const updateLinkParamsSchema = z.object({
  nodeId: z.string(),
  linkId: z.string(),
  body: z.object({
    expiresAt: z.string().nullable(),
    addEmails: z.array(z.string().email()).optional(),
    removeEmails: z.array(z.string().email()).optional(),
  }),
});

export type CreateLinkParams = z.infer<typeof createLinkParamsSchema>;
export type UpdateLinkParams = z.infer<typeof updateLinkParamsSchema>;
export type Link = z.infer<typeof linkSchema>;
export type LinkList = z.infer<typeof linkListSchema>;
export type LinkCreateResult = z.infer<typeof linkCreateResultSchema>;
export type ResolvedLink = z.infer<typeof resolvedLinkSchema>;

export type EditCreateSubmissionLinkModalProps =
  | { mode: 'create'; nodeId: string; folderName: string }
  | { mode: 'edit'; nodeId: string; folderName: string; link: Link };

export const createInviteContributorFormSchema = (minDate: Date, tooEarlyMessage: string) =>
  z.object({
    pendingEmails: z.array(z.string().email()).nullable().optional(),
    expiresAt: z
      .string()
      .superRefine((value, ctx) => {
        const result = validateDateInputString(value, { minYear: minDate.getFullYear() });
        if (!result.valid) {
          ctx.addIssue({ code: z.ZodIssueCode.custom, message: result.message });
          return;
        }

        const parsedDate = parseDateTimeInput(value, DateTimeGranularity.Day);
        if (!parsedDate || parsedDate < minDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: tooEarlyMessage,
          });
        }
      })
      .nullable()
      .optional(),
  });

export type InviteContributorFormValues = z.infer<ReturnType<typeof createInviteContributorFormSchema>>;
